/**
 * "Is CDID keeping up with its own queue?" — the headline question for
 * `/admin/automation/cdid`, answered as a 14-day flow balance rather than a
 * snapshot. A snapshot count only ever says how big the pile is *right now*;
 * this section says whether it is growing or shrinking, and by how much.
 *
 * The verdict line comes first and states the arithmetic in plain English —
 * an operator should never have to read the chart to know whether CDID is
 * winning or losing. The chart exists to show the *shape* of that balance
 * (is it a steady win, or three bad days masked by one good one), not to
 * carry the verdict itself.
 *
 * `queueEnd` (the reconstructed depth) is the primary series — it is the
 * thing an operator is actually worried about — and gets its own Y axis so
 * its narrow week-to-week band (roughly 100–200 in the reference data) isn't
 * flattened by sharing a 0-based scale with the much smaller daily flow bars
 * (roughly 15–65). `arrivals` / `drained` are bars on the left axis: the
 * day-to-day flow that produces the line. `completed` is a genuinely
 * different question from `drained` (CDID's own "finished" vs. "left the
 * queue" — see `CdidFlowDay.completed`'s comment) and is deliberately kept
 * out of the bar/line contest for attention; it rides in the tooltip only.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Equal, RefreshCw } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CdidFlowDay, CdidFlowHistory } from '@a7y/types';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/useTheme';

const REFETCH_INTERVAL_MS = 5 * 60_000;

/**
 * Same validated triad as `audit-spend/DailyTrendChart.tsx` (CVD ΔE 8.9+,
 * normal-vision ΔE 19.8+, contrast checked against both surfaces) — reused
 * rather than inventing a fresh palette, so the same three roles (a warm
 * "load coming in", a cool "load leaving", a blue "the thing being tracked")
 * keep the same hues everywhere they appear in this admin console.
 */
const COLORS = {
  light: { arrivals: '#f59e0b', drained: '#10b981', queueEnd: '#3b82f6' },
  dark: { arrivals: '#c98500', drained: '#199e70', queueEnd: '#3987e5' },
} as const;

/** `YYYY-MM-DD` → `2 Aug`. `date` is a UTC calendar day, not an instant — this
 *  formats in UTC deliberately. Running it through `fmtLocal` (which converts
 *  an instant to the viewer's zone) would shift the label a day for any
 *  negative-offset viewer, turning a correct date into a wrong one. */
function formatDayShort(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short' }).format(
    d
  );
}

/** `YYYY-MM-DD` → `Sat 2 Aug 2026` (UTC), for the tooltip header. */
function formatDayLong(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

const VERDICT_META: Record<
  CdidFlowHistory['verdict'],
  { icon: typeof CheckCircle2; classes: string; label: string }
> = {
  'keeping-up': {
    icon: CheckCircle2,
    classes:
      'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
    label: 'Keeping up',
  },
  // Never a reassuring colour — this is the state an operator needs to act on.
  'falling-behind': {
    icon: AlertTriangle,
    classes:
      'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300',
    label: 'Falling behind',
  },
  // A tie is neither win nor loss — kept visually distinct from both, not a
  // muted version of "keeping up".
  even: {
    icon: Equal,
    classes:
      'border-zinc-300 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300',
    label: 'Holding even',
  },
};

/**
 * The plain-English arithmetic behind the verdict — the line an operator should
 * be able to read without ever looking at the chart.
 *
 * THE FLOW DELTA AND THE DEPTH ARE NEVER CHAINED, and that is the whole point
 * of this function's shape. It used to read "queue up 3 to 0", which asserts
 * that the two numbers are the two ends of one subtraction and so implies the
 * queue began at MINUS three. They are not ends of anything: `net` is flow
 * measured against the state-name set {Triage, Backlog, Todo} from the BigQuery
 * ledger plus Linear creations, while `eligibleNow` is a live depth measured
 * against a far narrower population — admitted AND unclaimed after the whole
 * CDID funnel (delegation, runnable project, active initiative, due date,
 * blockers, settling window). A ticket sitting in Todo in a gated project is an
 * arrival that can never be an `eligibleNow`, so no start depth reconciles the
 * two. The backend already knows this: it clamps its reconstructed per-day
 * depth at zero, which on this data draws a flat zero line directly under a
 * banner claiming the queue rose.
 *
 * So the delta is stated as flow ("arrivals ran 3 ahead") and the depth is
 * stated separately as a snapshot. Both are true; neither is presented as the
 * consequence of the other.
 */
function verdictSentence(history: CdidFlowHistory): string {
  const { totals, eligibleNow } = history;
  const days = history.days.length;
  const delta = Math.abs(totals.net);
  const eligible = `${eligibleNow} eligible right now`;
  if (history.verdict === 'even') {
    return `Drained ${totals.drained} matched ${totals.arrivals} arrivals over ${days} days; ${eligible}.`;
  }
  const lead = totals.net < 0 ? 'drain ran' : 'arrivals ran';
  return `Drained ${totals.drained} against ${totals.arrivals} arrivals over ${days} days — ${lead} ${delta} ahead; ${eligible}.`;
}

interface FlowTooltipPayloadEntry {
  payload?: CdidFlowDay;
}

type FlowColors = { arrivals: string; drained: string; queueEnd: string };

function FlowTooltip({
  active,
  payload,
  colors,
}: {
  active?: boolean;
  payload?: FlowTooltipPayloadEntry[];
  colors: FlowColors;
}) {
  if (!active || !payload?.length) return null;
  const day = payload[0]?.payload;
  if (!day) return null;

  return (
    <div className="rounded-lg border border-zinc-300 bg-white px-3 py-2 shadow-lg dark:border-zinc-600 dark:bg-zinc-800">
      <div className="text-[11px] text-zinc-500 dark:text-zinc-400">{formatDayLong(day.date)}</div>
      <ul className="mt-1 space-y-0.5 text-xs">
        <li className="flex items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: colors.arrivals }}
          />
          <span className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {day.arrivals}
          </span>
          <span className="text-zinc-500 dark:text-zinc-400">
            arrivals ({day.created} created, {day.returned} returned)
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: colors.drained }}
          />
          <span className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {day.drained}
          </span>
          <span className="text-zinc-500 dark:text-zinc-400">drained</span>
        </li>
        {/* Deliberately not a bar/line — see the file header on why `completed`
            must not compete with `drained` for visual attention. */}
        <li className="flex items-center gap-2 text-zinc-400 dark:text-zinc-500">
          <span className="inline-block h-2.5 w-2.5 rounded-sm border border-dashed border-current" />
          <span className="font-semibold tabular-nums">{day.completed}</span>
          <span>completed (Done in Dev)</span>
        </li>
        <li className="flex items-center gap-2 border-t border-zinc-100 pt-1 dark:border-zinc-700">
          <span
            className="inline-block h-0.5 w-3 rounded-full"
            style={{ backgroundColor: colors.queueEnd }}
          />
          <span className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {day.queueEnd}
          </span>
          <span className="text-zinc-500 dark:text-zinc-400">queue at end of day</span>
        </li>
      </ul>
    </div>
  );
}

function LoadingState() {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex items-center gap-2 text-sm text-zinc-500">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Reading CDID queue flow…
      </div>
    </section>
  );
}

function ErrorState() {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      Queue flow failed to load. Check the Worker logs for /admin/autobuild/cdid/flow.
    </div>
  );
}

export function CdidFlowSection() {
  const { resolvedTheme } = useTheme();
  const colors = COLORS[resolvedTheme === 'dark' ? 'dark' : 'light'];
  const gridStroke = resolvedTheme === 'dark' ? '#3f3f46' : '#e4e4e7';
  const axisTick = { fontSize: 11, fill: '#9ca3af' };

  const query = useQuery({
    queryKey: ['cdid-flow-history'],
    queryFn: () => api.getCdidFlowHistory(),
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  if (query.isError) return <ErrorState />;
  if (!query.data) return <LoadingState />;

  const history = query.data;
  const meta = VERDICT_META[history.verdict];
  const Icon = meta.icon;
  const isShortWindow = history.ledgerCoverageDays < history.windowDays;

  return (
    <section className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div>
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Queue flow — is CDID keeping up?
        </h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          Bot-delegated tickets arriving into the eligible pool against tickets leaving it, over the
          trailing window.
        </p>
      </div>

      <div
        role="status"
        className={cn(
          'flex items-start gap-2 rounded-md border px-3 py-2 text-sm font-medium',
          meta.classes
        )}
      >
        <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          {meta.label} — {verdictSentence(history)}
        </span>
      </div>

      {isShortWindow && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Showing {history.ledgerCoverageDays} of {history.windowDays} requested days — the
          ticket-state ledger doesn&apos;t go back further yet. This is a short series, not a wrong
          one.
        </p>
      )}

      <div className="h-[220px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={history.days} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tick={axisTick}
              axisLine={{ stroke: gridStroke, strokeWidth: 1 }}
              tickLine={false}
              dy={6}
              minTickGap={16}
              tickFormatter={formatDayShort}
            />
            {/* Left axis — the daily flow bars. Its own 0-based scale so the
                smaller arrivals/drained values (roughly 15–65 in a typical
                window) get real bar height instead of being squashed under
                queueEnd's much larger range. */}
            <YAxis
              yAxisId="flow"
              width={36}
              tick={axisTick}
              axisLine={false}
              tickLine={false}
            />
            {/* Right axis — reconstructed queue depth. Kept off the flow axis
                on purpose: queueEnd's meaningful week-to-week variation sits in
                a narrow band relative to its own 0-based range, and sharing
                the flow axis would flatten that band into a nearly flat line. */}
            <YAxis
              yAxisId="queue"
              orientation="right"
              width={36}
              tick={axisTick}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<FlowTooltip colors={colors} />}
              cursor={{ fill: 'rgba(148,163,184,0.12)' }}
            />
            <Bar
              yAxisId="flow"
              dataKey="arrivals"
              name="Arrivals"
              fill={colors.arrivals}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
              isAnimationActive={false}
            />
            <Bar
              yAxisId="flow"
              dataKey="drained"
              name="Drained"
              fill={colors.drained}
              radius={[2, 2, 0, 0]}
              maxBarSize={18}
              isAnimationActive={false}
            />
            <Line
              yAxisId="queue"
              type="monotone"
              dataKey="queueEnd"
              name="Queue depth"
              stroke={colors.queueEnd}
              strokeWidth={2.5}
              dot={{ r: 2.5, strokeWidth: 0, fill: colors.queueEnd }}
              activeDot={{ r: 4.5 }}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colors.arrivals }} />
          arrivals
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: colors.drained }} />
          drained
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: colors.queueEnd }} />
          queue depth (right axis)
        </span>
        <span className="text-zinc-400">completed count is in the tooltip only</span>
      </div>

      {history.unreadable.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Could not read: {history.unreadable.join(', ')}
        </p>
      )}

      {/* The ledger has no notion of delegation, so hand-moved tickets on the
          ADDIS / APHID / UKKO boards would otherwise count as CDID draining —
          34% of departures when measured. Saying how many were dropped is what
          keeps the verdict honest about whose work it is describing. */}
      {history.excludedNonDelegated > 0 && (
        <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
          Excluded {history.excludedNonDelegated} transition
          {history.excludedNonDelegated === 1 ? '' : 's'} on tickets not delegated to the bot —
          hand-moved work is not CDID's to drain.
        </p>
      )}

      <p className="text-[11px] italic text-zinc-400">{history.reconstructionCaveat}</p>
    </section>
  );
}
